import React, { useMemo, useState } from "react";
import { Navigate } from "@tanstack/react-router";
import {
  Box,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Button,
  Icon,
  Separator,
  Skeleton,
  SkeletonGroup,
  TabsViewIcon,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  useIsMobile,
} from "@salestrak/ui";

import { PageHeaderBreadcrumb } from "#app/components/page-header";
import { DashboardBoardSkeletonPane } from "#app/widgets/dashboards/dashboard-board-skeleton";
import { PermissionGate } from "#app/components/permissions/permission-gate";
import { viewTabVisual } from "#app/components/view-options";
import type { EntityViewRow } from "#app/lib/data";
import { useWorkspace } from "#app/lib/data/workspace-context";
import { IssueFilterProvider } from "#app/lib/filters/issue-filter-context";
import { AnalyticsScope } from "#app/lib/analytics";
import type { IssueScope } from "#app/lib/filters/issue-scope";
import { VIEW_PANE_ANCHOR_ID } from "#app/lib/hooks/use-anchor-insets";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { canManageSharing } from "#app/lib/utils/access-level";
import { ViewsProvider, useViews } from "#app/lib/views/view-context";
import { Dashboard } from "#app/widgets/dashboards/dashboard";
import { DashboardShareDialog } from "./dashboard-share-dialog";
import { useDashboardViewFavorite } from "./use-dashboard-favorites";
import { useRecordDashboardVisit } from "./use-recent-dashboards";
import { routePath } from "#app/lib/hooks/use-app-navigate";

/**
 * A WORKSPACE-scoped dashboard as its own page: `/dashboards/$viewId`.
 *
 * The row is a dashboard-type entity view whose scope is the workspace, so it
 * appears only in the Dashboards module — never in a workflow's tab strip —
 * and reads from the workflow set it names (`workflowIds`). The page is the
 * workflow shell's masthead grammar with one crumb trail ("Dashboards / the
 * view") instead of a tab strip, and it mounts the same two providers the
 * shell does: the views scope (the display-options panel, Save, rename and
 * delete all read it) and the screen's filter set, keyed per view.
 *
 * The row arrives with its workflow set already named — `NewDashboardDialog`
 * asks for it before the row exists, and the server refuses a dashboard that
 * reads from nothing — so a fresh dashboard opens straight on the ordinary
 * empty-board template chooser. The set is edited afterwards in the
 * display-options panel's Workflows page, like every other view setting.
 *
 * Access is the row's own: private to its creator until they share it or
 * make it public. The Share action needs `create.views` AND share management
 * on the row (the server stacks the same two gates).
 */
export function WorkspaceDashboardPage({ viewId }: { viewId: string }) {
  const { activeWorkspace } = useWorkspace();
  const workspaceId = activeWorkspace?.workspaceId ?? "";
  const scope = useMemo(
    () => ({ kind: "workspace" as const, id: workspaceId }),
    [workspaceId],
  );
  if (!workspaceId) return null;
  return (
    <ViewsProvider key={viewId} scope={scope} activeKey={viewId}>
      <DashboardFilterScope>
        <WorkspaceDashboardBody />
      </DashboardFilterScope>
    </ViewsProvider>
  );
}

/**
 * The screen's filter set, keyed per view: inside the views scope the
 * provider binds to the active view's config draft, and the key makes that
 * a mount-time read that lands once the row has loaded.
 */
function DashboardFilterScope({ children }: { children: React.ReactNode }) {
  const { activeView } = useViews();
  return (
    <IssueFilterProvider key={activeView?.id ?? ""} analyticsScope={AnalyticsScope.Workflows}>
      {children}
    </IssueFilterProvider>
  );
}

/** The one-word state the masthead glyph and the directory name glyph share. */
export function dashboardVisibilityLabel(
  view: Pick<EntityViewRow, "visibility" | "isShared">,
): { icon: "lock" | "users" | "globe"; label: string } {
  if (view.visibility === "public") return { icon: "globe", label: "Public" };
  if (view.isShared) return { icon: "users", label: "Shared" };
  return { icon: "lock", label: "Private" };
}

function WorkspaceDashboardBody() {
  const { activeWorkspace } = useWorkspace();
  const workspacePath = activeWorkspace?.urlPath ?? "";
  const workspaceId = activeWorkspace?.workspaceId ?? "";
  const { push } = useAppNavigate();
  const { scope: viewsScope, activeView, initialLoading } = useViews();
  const favorite = useDashboardViewFavorite(activeView, viewsScope, "masthead");
  const [shareOpen, setShareOpen] = useState(false);
  // A phone gives the title the row: the "Dashboards" crumb goes (the ×
  // is the way back) and Share drops its word.
  const isMobile = useIsMobile();
  useRecordDashboardVisit("workspace", workspaceId, activeView);

  const workflowIds = activeView?.workflowIds ?? [];
  const workflowIdsKey = workflowIds.join(",");
  const scope = useMemo<IssueScope>(
    () => ({ kind: "workflows", workflowIds: workflowIdsKey ? workflowIdsKey.split(",") : [] }),
    [workflowIdsKey],
  );

  const canShare = canManageSharing(activeView?.viewerAccessLevel);

  // The row arrives with its query, so a direct link lands here before the
  // view it names exists on the client.
  const dashboardsPath = `/${workspacePath}/dashboards`;

  // The page's own silhouette while the row loads: the masthead line with a
  // name-length bar where the view's identity will sit, then the board
  // stand-in. Delayed as one, so a cached row paints the page and never a bar.
  if (initialLoading) {
    return (
      <SkeletonGroup className="flex min-h-0 min-w-0 flex-1 flex-col" aria-hidden>
        <Box gap="sm" paddingTop="sm" paddingX="md" paddingBottom="sm" shrink={false}>
          <Box direction="row" align="center" justify="between" gap="sm" minHeight={28}>
            <Box direction="row" align="center" gap="sm">
              <Skeleton height={12} width={80} />
              <Skeleton height={12} width={8} />
              <Skeleton width={16} height={16} className="rounded-[4px]" />
              <Skeleton height={14} width={144} />
            </Box>
            <Skeleton height={24} width={24} className="rounded-md" />
          </Box>
        </Box>
        <Separator />
        <DashboardBoardSkeletonPane />
      </SkeletonGroup>
    );
  }

  // A deleted dashboard's URL outlives it, and a private one is invisible to
  // everyone but its audience — both land on the directory.
  if (!activeView) {
    return <Navigate to={routePath(dashboardsPath)} replace />;
  }

  const tabVisual = viewTabVisual(activeView);
  const visibility = dashboardVisibilityLabel(activeView);

  return (
    <Box fill="flex" direction="column" minHeight={0} minWidth={0}>
      {/* The entity shells' masthead rhythm: 8px top, 28px first line. No tab
        * strip — a workspace dashboard is one page, not one tab of many. */}
      <Box gap="sm" paddingTop="sm" paddingX="md" paddingBottom="sm" shrink={false}>
        <Box
          direction="row"
          align="center"
          justify="between"
          gap="sm"
          minHeight={28}
        >
          <PageHeaderBreadcrumb>
            {isMobile ? null : (
              <>
                <BreadcrumbItem>
                  <BreadcrumbLink
                    href={dashboardsPath}
                    onClick={(event: React.MouseEvent) => {
                      event.preventDefault();
                      push("/dashboards");
                    }}
                  >
                    Dashboards
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
              </>
            )}
            <BreadcrumbItem>
              {/* The view's own identity — the same tile its directory row
                  wears. Rename and the icon picker live in the display-options
                  panel, like every saved view's. */}
              <BreadcrumbPage>
                <Box direction="row" align="center" gap="sm" minWidth={0}>
                  {tabVisual.custom ? (
                    <Icon name={tabVisual.icon} size="md" />
                  ) : (
                    <TabsViewIcon
                      color={tabVisual.color}
                      icon={tabVisual.icon}
                      variant="compact-tile"
                    />
                  )}
                  <Text size={null} truncate>
                    {activeView.name}
                  </Text>
                </Box>
              </BreadcrumbPage>
            </BreadcrumbItem>
          </PageHeaderBreadcrumb>

          <Box direction="row" align="center" gap="sm" shrink={false}>
            {/* The visibility glyph names its state to every reader: a
                sharer presses it to open Sharing (the state's own door); for
                anyone else it is a labelled, focusable image, so the tooltip
                reaches a keyboard too. */}
            <Tooltip>
              <TooltipTrigger
                render={
                  canShare ? (
                    <Button
                      aria-label={`${visibility.label}: manage sharing`}
                      onClick={() => setShareOpen(true)}
                      size="icon-sm"
                      variant="ghost"
                    />
                  ) : (
                    <Box
                      direction="row"
                      align="center"
                      role="img"
                      aria-label={visibility.label}
                      tabIndex={0}
                    />
                  )
                }
              >
                <Icon color="muted" name={visibility.icon} size="sm" />
              </TooltipTrigger>
              <TooltipPopup side="bottom">{visibility.label}</TooltipPopup>
            </Tooltip>
            {/* The viewer's own star — the directory row's hover action, here
                on the page itself. Lit = FILLED in the favorite's gold (the
                same glyph and tight drop-shadow the directory lights). */}
            {favorite ? (
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button
                      aria-label={
                        favorite.favorited
                          ? "Remove from favorites"
                          : "Add to favorites"
                      }
                      onClick={favorite.toggle}
                      size="icon-sm"
                      variant="ghost"
                    >
                      <Icon
                        name={favorite.favorited ? "star-filled" : "star"}
                        color={favorite.favorited ? "warning" : "muted"}
                        size="sm"
                        className={
                          favorite.favorited
                            ? "drop-shadow-[0_1px_1px_rgb(0_0_0/0.35)]"
                            : undefined
                        }
                      />
                    </Button>
                  }
                />
                <TooltipPopup side="bottom">
                  {favorite.favorited ? "Remove from favorites" : "Add to favorites"}
                </TooltipPopup>
              </Tooltip>
            ) : null}
            {canShare ? (
              // Sharing or publishing a dashboard is the same act as making a
              // workspace-wide view, so it wears the same role gate — shown
              // disabled with the reason, never hidden from an owner.
              <PermissionGate perm="create.views">
                <Button
                  variant="ghost"
                  size={isMobile ? "icon-sm" : "sm"}
                  onClick={() => setShareOpen(true)}
                  aria-label="Share dashboard"
                >
                  <Icon name="users" size="sm" color="muted" />
                  {isMobile ? null : <Text variant="muted">Share</Text>}
                </Button>
              </PermissionGate>
            ) : null}
            {/* Leaves the dashboard for the directory it was opened from. */}
            <Button
              aria-label="Close dashboard"
              onClick={() => push("/dashboards")}
              size="icon-sm"
              variant="ghost"
            >
              <Icon color="muted" name="x" size="sm" />
            </Button>
          </Box>
        </Box>
      </Box>

      <Separator />

      {/* Anchor for the display-options and manage panels: they pin to this
          region so the masthead stays visible above them. */}
      <Box
        id={VIEW_PANE_ANCHOR_ID}
        direction="column"
        fill="flex"
        minHeight={0}
        minWidth={0}
        overflow="hidden"
      >
        <Dashboard
          key={activeView.id}
          scope={scope}
          // The server derives the view's widget key on the row, so the
          // board this renders and the board the API accepts writes to are
          // one value with one author.
          viewKey={activeView.widgetView ?? undefined}
        />
      </Box>

      <DashboardShareDialog
        view={shareOpen ? activeView : null}
        onOpenChange={(open) => {
          if (!open) setShareOpen(false);
        }}
      />
    </Box>
  );
}
